import React, { useMemo, useState } from 'react';
import { Slider } from '@mui/material';
import moment from 'moment';
import { generateSampleURL } from 'new/elements/MacroEditor/utils/macroUtils';
import classes from './MacroBuilder.module.scss';

/**
 * Redesigned macro-insertion popup (prototype MacroBuilder): a "Time macro" tab
 * (slider + unit/format + live preview + Insert) and a "Lookup macro" tab
 * (grouped list of selected-lookup columns).
 *
 * Grammar reconciliation (existing config wins over the prototype's `{now+4d|iso}`):
 *   - Time macro inserts the REAL bare grammar `{now}` / `{now±N}`.
 *   - The unit + format dropdowns write the STEP-LEVEL `date.time.unit` /
 *     `date.format` keys (via onChangeSetting), exactly like the legacy form.
 *   - Lookup macro inserts the existing macro name (`{lookup{id}.{col}}`),
 *     sourced from the same suggestion list the editor already uses.
 */

interface FieldOption {
  label: string;
  value: string;
  /** optional sub-text shown under the label in the dropdown menu */
  sub?: string;
}

/** Format a fixed sample date by a backend date.format pattern (for examples). */
const SAMPLE_DATE = '2020-04-24T13:24:05';
const normalizePattern = (p: string): string => p.replace(/yyyy/g, 'YYYY').replace(/dd/g, 'DD');
const formatExample = (fmt: string): string => {
  try {
    return moment.utc(SAMPLE_DATE).format(normalizePattern(fmt));
  } catch {
    return '';
  }
};

interface MacroSuggestion {
  name: string;
  display_name: string;
  type: string;
  type_display: string;
  /** Placeholder marker for a selected lookup that has no columns yet (shows "(empty)"). */
  empty?: boolean;
}

export interface MacroBuilderProps {
  /** suggestion list (from getMentionsFromMacroAttrs) — used for lookup/step tabs */
  macros: MacroSuggestion[];
  dateFormat: string;
  dateTimeUnit: string;
  dateFormatOptions: FieldOption[];
  timeUnitOptions: FieldOption[];
  /** writes step-level date.format / date.time.unit */
  onChangeSetting: (key: string, value: string) => void;
  /** insert the raw macro at the editor cursor */
  onInsert: (raw: string) => void;
  onCancel: () => void;
}

const SLIDER_RANGE = 30;

const serializeTimeMacro = (sign: '+' | '-', amount: number): string =>
  amount === 0 ? '{now}' : `{now${sign}${amount}}`;

const withCurrent = (options: FieldOption[], current: string): FieldOption[] =>
  !current || options.some((o) => o.value === current)
    ? options
    : [{ label: current, value: current }, ...options];

/**
 * Inline dropdown whose trigger sizes to the SELECTED value (dynamic width),
 * unlike a native <select> (which reserves space for the widest option). The menu
 * renders inside the popup's Paper so the macro popup's ClickAwayListener treats
 * option clicks as "inside".
 */
const InlineSelect: React.FC<{
  value: string;
  options: FieldOption[];
  onChange: (value: string) => void;
  testid?: string;
  ariaLabel?: string;
}> = ({ value, options, onChange, testid, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const opts = withCurrent(options, value);
  const current = opts.find((o) => o.value === value);
  return (
    <span className={classes.inlineSelect}>
      <button
        type="button"
        className={classes.inlineSelectBtn}
        onClick={() => setOpen((o) => !o)}
        data-testid={testid}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
      >
        <span className={classes.inlineSelectValue}>{current?.label ?? value}</span>
        <span className={classes.inlineSelectCaret} aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <ul className={classes.inlineSelectMenu} role="listbox">
          {opts.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                className={classes.inlineSelectOption}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                <span className={classes.optionLabel}>{o.label}</span>
                {o.sub && <span className={classes.optionSub}>{o.sub}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
};

interface MacroGroup {
  key: string;
  name: string;
  items: MacroSuggestion[];
}

const groupByPrefix = (macros: MacroSuggestion[], prefix: string): MacroGroup[] => {
  const groups: MacroGroup[] = [];
  const byType = new Map<string, number>();
  for (const m of macros) {
    if (!m.type || !m.type.startsWith(prefix)) continue;
    const idx = byType.get(m.type);
    if (idx === undefined) {
      byType.set(m.type, groups.length);
      groups.push({ key: m.type, name: m.type_display || m.type, items: [m] });
    } else {
      groups[idx].items.push(m);
    }
  }
  return groups;
};

export const MacroBuilder: React.FC<MacroBuilderProps> = ({
  macros,
  dateFormat,
  dateTimeUnit,
  dateFormatOptions,
  timeUnitOptions,
  onChangeSetting,
  onInsert,
  onCancel,
}) => {
  const lookupGroups = useMemo(() => groupByPrefix(macros, 'lookup'), [macros]);
  const stepGroups = useMemo(() => groupByPrefix(macros, 'step'), [macros]);
  const hasSteps = stepGroups.length > 0;

  // Format options: friendlier labels for epoch/ISO (UI-only — values unchanged),
  // a worked example for the rest ("E.g. 2020-04-24"). No example for epoch/ISO
  // (epoch examples are meaningless via moment and ISO is long).
  const formatOptions = useMemo(
    () =>
      dateFormatOptions.map((o) => {
        const l = o.label.toLowerCase();
        let label = o.label;
        let noExample = false;
        if (l.includes('epoch') && l.includes('milli')) {
          label = 'Unix Epoch (ms)';
          noExample = true;
        } else if (l.includes('epoch') && l.includes('sec')) {
          label = 'Unix Epoch (secs)';
          noExample = true;
        } else if (l.includes('iso')) {
          label = 'ISO-8601 date';
          noExample = true;
        }
        const ex = noExample ? '' : formatExample(o.value);
        return ex ? { value: o.value, label, sub: `E.g. ${ex}` } : { value: o.value, label };
      }),
    [dateFormatOptions],
  );

  const [tab, setTab] = useState<'time' | 'lookup' | 'step'>('time');
  const [sign, setSign] = useState<'+' | '-'>('+');
  const [amount, setAmount] = useState<number>(1);
  // Slider scale grows when the thumb reaches an end (and always fits a typed amount),
  // so the user can keep moving past the initial ±SLIDER_RANGE.
  const [range, setRange] = useState<number>(SLIDER_RANGE);
  const effectiveRange = Math.max(range, amount);

  const raw = serializeTimeMacro(sign, amount);
  const preview = useMemo(() => {
    try {
      return generateSampleURL(raw, null as any, null as any, dateFormat, dateTimeUnit);
    } catch {
      return '';
    }
  }, [raw, dateFormat, dateTimeUnit]);

  const onSlider = (_e: Event, v: number | number[]) => {
    const n = Array.isArray(v) ? v[0] : v;
    setSign(n < 0 ? '-' : '+');
    setAmount(Math.abs(n));
    // Reached an end of the scale — grow it so the user can keep dragging further.
    if (Math.abs(n) >= effectiveRange) {
      setRange(effectiveRange + SLIDER_RANGE);
    }
  };

  // Stepper operates on the SIGNED offset so it crosses zero (now+0 → down → now−1),
  // flipping the sign — unlike the native number-input spinner which clamps at 0.
  const signedOffset = sign === '-' ? -amount : amount;
  const stepOffset = (delta: number) => {
    const next = signedOffset + delta;
    setSign(next < 0 ? '-' : '+');
    setAmount(Math.abs(next));
  };

  return (
    <div className={classes.builder} data-testid="macro-builder">
      <div className={classes.tabs}>
        <button
          type="button"
          className={tab === 'time' ? classes.tabActive : classes.tab}
          onClick={() => setTab('time')}
          data-testid="macro-tab-time"
        >
          Time macro
        </button>
        <button
          type="button"
          className={tab === 'lookup' ? classes.tabActive : classes.tab}
          onClick={() => setTab('lookup')}
          data-testid="macro-tab-lookup"
        >
          Lookup macro
        </button>
        {hasSteps && (
          <button
            type="button"
            className={tab === 'step' ? classes.tabActive : classes.tab}
            onClick={() => setTab('step')}
            data-testid="macro-tab-step"
          >
            Step value
          </button>
        )}
      </div>

      {tab === 'time' && (
        <div className={classes.timeTab}>
          <div className={classes.sliderLabels}>
            <span>−{effectiveRange}</span>
            <span className={classes.now}>now</span>
            <span>+{effectiveRange}</span>
          </div>
          <Slider
            min={-effectiveRange}
            max={effectiveRange}
            step={1}
            value={sign === '-' ? -amount : amount}
            onChange={onSlider}
            data-testid="macro-slider"
          />
          <div className={classes.offsetRow}>
            <span className={classes.mono}>
              {'{'} now {amount === 0 ? '' : sign === '-' ? '−' : '+'} {amount === 0 ? '' : amount}{' '}
              {'}'}
            </span>
            <span>in</span>
            <InlineSelect
              value={dateTimeUnit}
              options={timeUnitOptions}
              onChange={(v) => onChangeSetting('date.time.unit', v)}
              testid="macro-unit"
              ariaLabel="Time unit"
            />
            <InlineSelect
              value={dateFormat}
              options={formatOptions}
              onChange={(v) => onChangeSetting('date.format', v)}
              testid="macro-format"
              ariaLabel="Date format"
            />
            <span>format</span>
          </div>

          <div className={classes.preview} data-testid="macro-preview">
            <span className={classes.mono}>
              {/* Sign stays outside; the input is the magnitude and is ALWAYS rendered
                  (shows "now + 0" at zero instead of vanishing). The macro itself
                  serializes "now + 0" down to {now} (serializeTimeMacro). Cross zero to
                  the past via the slider, which flips the sign. */}
              {'{'} now {sign === '-' ? '−' : '+'}{' '}
              <span className={classes.amountField}>
                <input
                  type="number"
                  min={0}
                  className={classes.amountInput}
                  value={amount}
                  onChange={(e) => setAmount(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                  data-testid="macro-amount"
                  aria-label="Macro offset amount"
                />
                <span className={classes.amountStepper}>
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label="Increase offset"
                    onClick={() => stepOffset(1)}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label="Decrease offset"
                    onClick={() => stepOffset(-1)}
                  >
                    ▼
                  </button>
                </span>
              </span>{' '}
              {(
                timeUnitOptions.find((o) => o.value === dateTimeUnit)?.label ?? dateTimeUnit
              ).toLowerCase()}{' '}
              {'}'}
            </span>
            <span className={classes.eq}>=</span>
            <span className={classes.previewValue}>{preview}</span>
          </div>
        </div>
      )}

      {tab === 'lookup' && (
        <div className={classes.listTab}>
          <div className={classes.listHint}>Insert a value from a selected list</div>
          {lookupGroups.length === 0 ? (
            <p className={classes.empty}>
              No lists selected yet. Use “+ Add Lookup” above the steps to make list columns
              available here.
            </p>
          ) : (
            lookupGroups.map((g) => {
              const columns = g.items.filter((m) => !m.empty);
              return (
                <div key={g.key} className={classes.group}>
                  <div className={classes.groupName}>
                    {g.name}
                    {columns.length === 0 && <span className={classes.emptyNote}> (empty)</span>}
                  </div>
                  {columns.map((m) => (
                    <button
                      type="button"
                      key={m.name}
                      className={classes.listItem}
                      onClick={() => onInsert(m.name)}
                      data-testid={`macro-lookup-${m.name}`}
                    >
                      {g.name}.<span className={classes.attr}>{m.display_name}</span>
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}

      {tab === 'step' && (
        <div className={classes.listTab}>
          <div className={classes.listHint}>Insert a field from a previous step’s record</div>
          {stepGroups.map((g) => (
            <div key={g.key} className={classes.group}>
              <div className={classes.groupName}>{g.name}</div>
              {g.items.map((m) => (
                <button
                  type="button"
                  key={m.name}
                  className={classes.listItem}
                  onClick={() => onInsert(m.name)}
                  data-testid={`macro-step-${m.name}`}
                >
                  {m.display_name}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className={classes.actions}>
        <button
          type="button"
          className={classes.cancel}
          onClick={onCancel}
          data-testid="macro-cancel"
        >
          Cancel
        </button>
        {tab === 'time' && (
          <button
            type="button"
            className={classes.insert}
            onClick={() => onInsert(raw)}
            data-testid="macro-insert"
          >
            Insert time macro
          </button>
        )}
      </div>
    </div>
  );
};

export default MacroBuilder;
